import React from 'react';
import Button from '@amzn/meridian/button';

import { useAppDispatch, useAppSelector } from '../../store';
import { showError } from '../../store/slices/errorSlice';
import { getUserName } from '../../services/boson/bosonClient';
import { WorkItemDetailStatus } from '../../utils/constants';

import { resolveWorkItem, selectResolveStatus } from './slice';

/**
 * Bottom-of-page action that marks the currently-loaded work item as
 * resolved. Reads the operator's login via `getUserName` at click time,
 * dispatches the `resolveWorkItem` thunk, and leaves the thunk-owned
 * error title ("Failed to Resolve Task") to the slice (see
 * `slice.ts` → `resolveWorkItem` rejected branch).
 *
 * Responsibilities owned here:
 *   - Disable while `resolveStatus === LOADING` so duplicate clicks
 *     are impossible — the slice uses `LOADING` as the in-flight marker.
 *   - Guard against a missing operator alias: if `getUserName` returns
 *     blank or throws, dispatch
 *     `showError({ title: "Unable to Determine Current User", ... })`
 *     and DO NOT dispatch the thunk.
 *
 * Side effects that live elsewhere:
 *   - Post-success navigation back to `/work-items` is handled by the
 *     parent detail-page component via an effect on `resolveStatus`.
 *   - Thunk-rejection error dispatch ("Failed to Resolve Task") is
 *     owned by the slice.
 */

interface ResolveTaskButtonProps {
    workItemId: string;
}

const ResolveTaskButton: React.FC<ResolveTaskButtonProps> = ({ workItemId }) => {
    const dispatch = useAppDispatch();
    const resolveStatus = useAppSelector(selectResolveStatus);
    const isResolving = resolveStatus === WorkItemDetailStatus.LOADING;

    const onClick = async (): Promise<void> => {
        if (isResolving) return;

        let alias: string;
        try {
            alias = await getUserName();
        } catch (err: any) {
            dispatch(
                showError({
                    title: 'Unable to Determine Current User',
                    message:
                        err?.message ??
                        'Unable to determine the current operator. Please sign in again.',
                }),
            );
            return;
        }

        if (!alias || alias.trim() === '') {
            dispatch(
                showError({
                    title: 'Unable to Determine Current User',
                    message:
                        'Unable to determine the current operator. Please sign in again.',
                }),
            );
            return;
        }

        // Thunk owns the "Failed to Resolve Task" showError on rejection,
        // so no further error handling is needed here.
        dispatch(resolveWorkItem({ workItemId, operator: alias }));
    };

    return (
        <Button
            type="primary"
            onClick={onClick}
            disabled={isResolving}
            data-testid="resolve-task-button"
        >
            Resolve Task
        </Button>
    );
};

export default ResolveTaskButton;
